import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  UserPlus, Crown, ShieldCheck, User, ShieldStar, ChartLineUp, Trash,
} from "@phosphor-icons/react";
import { ADMIN_EMAIL } from "../settings/shared";

export function TeamMembersCard({ workspace, onInviteMember, onRemoveMember, onChangeRole }) {
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviting, setInviting] = useState(false);
  const [contractorExpiry, setContractorExpiry] = useState("");

  const canManage = workspace.your_role === "owner" || workspace.your_role === "admin";

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    const payload = { email: inviteEmail.trim().toLowerCase(), role: inviteRole };
    if (inviteRole === "contractor" && contractorExpiry) {
      payload.expires_at = new Date(contractorExpiry).toISOString();
    }
    await onInviteMember(payload, inviteRole, contractorExpiry);
    setInviteEmail("");
    setContractorExpiry("");
    setInviting(false);
  };

  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-6">
      <h2 className="font-['Cormorant_Garamond'] text-lg font-semibold text-[var(--text-primary)] mb-4">
        Team Members ({workspace.members?.length || 0})
      </h2>

      <div className="space-y-3 mb-6">
        {workspace.members?.map((m) => {
          const isMemberAdmin = m.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase();
          const RoleIcon = isMemberAdmin ? ShieldStar : m.role === "owner" ? Crown : m.role === "admin" ? ShieldCheck : m.role === "manager" ? ChartLineUp : User;
          const roleLabel = isMemberAdmin ? "Master Admin" : m.role;
          const roleColor = isMemberAdmin ? "text-[#FF4400]" : m.role === "owner" ? "text-amber-500" : m.role === "admin" ? "text-blue-400" : m.role === "manager" ? "text-emerald-400" : m.role === "contractor" ? "text-purple-400" : "text-[var(--text-muted)]";
          return (
            <div key={m.membership_id} className="flex items-center gap-3 px-3 py-3 rounded-lg bg-[var(--bg-base)] border border-[var(--border)]" data-testid={`member-${m.email}`}>
              {m.picture ? (
                <img src={m.picture} alt="" className="w-8 h-8 rounded-full" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[var(--accent-subtle)] flex items-center justify-center">
                  <span className="text-xs font-medium text-[#FF4400]">{(m.name || m.email)[0]?.toUpperCase()}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[var(--text-primary)] truncate">{m.name || m.email}</p>
                <p className="text-xs text-[var(--text-muted)] truncate">{m.email}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1 text-xs text-[var(--text-muted)] capitalize">
                  <RoleIcon size={14} weight="fill" className={roleColor} />
                  {roleLabel}
                </span>
                {workspace.your_role === "owner" && m.role !== "owner" && (
                  <div className="flex items-center gap-1">
                    <select className="text-xs bg-transparent border border-[var(--border)] rounded px-1 py-0.5 text-[var(--text-secondary)]"
                      value={m.role} onChange={(e) => onChangeRole(m.membership_id, e.target.value)} data-testid={`role-select-${m.email}`}>
                      <option value="admin">Admin</option>
                      <option value="manager">Manager</option>
                      <option value="member">Member</option>
                      <option value="contractor">Contractor</option>
                    </select>
                    <button onClick={() => onRemoveMember(m.membership_id)}
                      className="p-1 rounded text-[var(--text-muted)] hover:text-red-400 hover:bg-red-400/10 transition-colors" data-testid={`remove-member-${m.email}`}>
                      <Trash size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {canManage && (
        <div className="pt-4 border-t border-[var(--border)]">
          <p className="font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-3">Invite Team Member</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input className="flex-1 bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]"
              value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="colleague@company.com" data-testid="invite-email-input" />
            <select className="bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)]"
              value={inviteRole} onChange={(e) => { setInviteRole(e.target.value); if (e.target.value !== "contractor") setContractorExpiry(""); }} data-testid="invite-role-select">
              <option value="member">Member</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
              <option value="contractor">Contractor</option>
            </select>
            {inviteRole === "contractor" && (
              <input
                type="date"
                value={contractorExpiry}
                onChange={(e) => setContractorExpiry(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                className="bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)]"
                data-testid="contractor-expiry-input"
                placeholder="Expires on"
              />
            )}
            <Button onClick={handleInvite} disabled={!inviteEmail.trim() || inviting || (inviteRole === "contractor" && !contractorExpiry)}
              className="bg-[#FF4400] hover:bg-[#FF6633] text-white gap-2" data-testid="invite-member-btn">
              <UserPlus size={16} weight="bold" />{inviting ? "Inviting..." : "Invite"}
            </Button>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
            <p className="text-xs text-[var(--text-muted)]">The user must sign in with Google first. Seat limit: {workspace.seat_limit || 26} total.</p>
            <p className="text-[10px] text-[var(--text-muted)]">
              <strong>Admin:</strong> System config + team management &nbsp;
              <strong>Manager:</strong> Analytics + approvals (read-only billing) &nbsp;
              <strong>Contractor:</strong> Time-limited access
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
